import type { GenUIEvent } from '../core/events';
import { textDelta, componentEvent, doneEvent } from '../core/events';
import { parseStreamingChunk, extractTextDelta, isFinalResponse } from '../core/parser';

/**
 * Process an EventSource stream and yield GenUIEvents
 */
export async function* processEventSource(
    eventSource: EventSource
): AsyncGenerator<GenUIEvent> {
    const messageQueue: MessageEvent[] = [];
    let resolveNext: ((value: MessageEvent | null) => void) | null = null;
    let isDone = false;

    const onMessage = (event: MessageEvent) => {
        if (resolveNext) {
            resolveNext(event);
            resolveNext = null;
        } else {
            messageQueue.push(event);
        }
    };

    const onError = () => {
        isDone = true;
        if (resolveNext) {
            resolveNext(null);
            resolveNext = null;
        }
    };

    eventSource.addEventListener('message', onMessage);
    eventSource.addEventListener('error', onError);

    try {
        while (!isDone) {
            const event = messageQueue.shift() ?? await new Promise<MessageEvent | null>((resolve) => {
                resolveNext = resolve;
            });

            if (!event) break;

            const data = event.data as string;

            if (data === '[DONE]') {
                yield doneEvent();
                break;
            }

            try {
                const parsed = JSON.parse(data);

                // Check if it's a final response with components
                if (isFinalResponse(parsed)) {
                    for (const component of parsed.data.components) {
                        yield componentEvent(component);
                    }
                    yield doneEvent();
                    break;
                }

                // Otherwise, it's a streaming chunk
                if (parsed.delta?.content) {
                    yield textDelta(parsed.delta.content);
                }
            } catch {
                // Skip malformed JSON
            }
        }
    } finally {
        eventSource.removeEventListener('message', onMessage);
        eventSource.removeEventListener('error', onError);
        eventSource.close();
    }
}
